/**
 * Debounce function to limit API calls during typing
 * @param func - Function to debounce
 * @param wait - Delay in milliseconds
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Mobile-optimized debounce with longer delay for touch devices
 * @param func - Function to debounce
 * @param wait - Base delay in milliseconds
 */
export function mobileDebounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number = 300
): (...args: Parameters<T>) => void {
  // Use longer delay on touch devices to reduce API calls during mobile typing
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const adjustedWait = isTouchDevice ? Math.max(wait, 500) : wait;
  
  return debounce(func, adjustedWait);
}
